// Budget store — hydrated from the on-device DB (P0 guest data never
// leaves the phone). Entries kept in memory for the current month.

import { create } from 'zustand';

import { getDb, KV_HOUSEHOLD, TABLES } from '@/db';
import { scheduleHouseholdSync } from '@/sync/household-sync';
import { useHouseholdStore } from '@/store/household-store';
import type { BudgetEntry, CategoryId, Household } from '@/types/domain';
import { todayIso, uuid } from '@/utils/format';

const KV_LAST_CATEGORY = 'last_category';

function monthPrefix(date = new Date()): string {
  return todayIso(date).slice(0, 7); // "2026-07"
}

// Derived values are PURE functions over subscribed state — never store
// getters (React Compiler memoizes stable store-method calls; see goals-store).
export function spentGrOf(entries: BudgetEntry[]): number {
  return entries.reduce((sum, e) => sum + e.amountGr, 0);
}

/** cumulative spending per day of month, index 0 = day 1, up to today */
export function cumulativeByDayGrOf(entries: BudgetEntry[]): number[] {
  const today = new Date().getDate();
  const byDay = new Array(today).fill(0);
  for (const e of entries) {
    const day = Number(e.entryDate.slice(8, 10));
    if (day >= 1 && day <= today) byDay[day - 1] += e.amountGr;
  }
  let running = 0;
  return byDay.map((v) => (running += v));
}

/**
 * Cumulative spending up to an ARBITRARY day — powers "stan wykresu na każdy
 * dzień" in the summary calendar. Honest by construction: entries carry real
 * entryDate, so the line for day N is a fact, not a reconstruction. Kept
 * separate from cumulativeByDayGrOf (which hard-codes "today" for the Start
 * card) so the shipped chart path stays untouched.
 */
export function cumulativeUpToDayGrOf(entries: BudgetEntry[], upToDay: number): number[] {
  const cap = Math.max(1, upToDay);
  const byDay = new Array(cap).fill(0);
  for (const e of entries) {
    const day = Number(e.entryDate.slice(8, 10));
    if (day >= 1 && day <= cap) byDay[day - 1] += e.amountGr;
  }
  let running = 0;
  return byDay.map((v) => (running += v));
}

/**
 * Entries of ANY month, read straight from the DB — hydrate() deliberately
 * loads only the current month into the store, but past rows survive in
 * SQLite; the calendar's prev-month chevron reads them lazily through here.
 */
export async function entriesForMonth(month: string): Promise<BudgetEntry[]> {
  const db = await getDb();
  const all = await db.list<BudgetEntry>(TABLES.entries);
  return all.filter((e) => e.entryDate.startsWith(month));
}

interface BudgetState {
  hydrated: boolean;
  monthlyBudgetGr: number;
  householdId: string;
  /** current month, newest first */
  entries: BudgetEntry[];
  lastCategory: CategoryId;
  hydrate: () => Promise<void>;
  /** note = optional free-text label ("LEGO", "kino") so a category shows WHAT
   *  it was spent on (owner/Maciek: kategoria za mało, chcę podpisać wydatek). */
  addEntry: (categoryId: CategoryId, amountGr: number, note?: string | null) => Promise<void>;
  /** Fix a mistyped expense (owner: user couldn't correct a wrong entry). */
  updateEntry: (id: string, patch: { categoryId?: CategoryId; amountGr?: number; note?: string | null }) => Promise<void>;
  /** Delete an expense added by mistake (owner: "nie da się usunąć"). */
  removeEntry: (id: string) => Promise<void>;
  setMonthlyBudget: (amountGr: number) => Promise<void>;
}

export const useBudgetStore = create<BudgetState>((set, get) => ({
  hydrated: false,
  monthlyBudgetGr: 0,
  householdId: '',
  entries: [],
  lastCategory: 'FOOD',

  hydrate: async () => {
    const db = await getDb();
    const householdRaw = await db.getKv(KV_HOUSEHOLD);
    const household = householdRaw ? (JSON.parse(householdRaw) as Household) : null;
    const all = await db.list<BudgetEntry>(TABLES.entries);
    const prefix = monthPrefix();
    const entries = all
      .filter((e) => e.entryDate.startsWith(prefix))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    const lastCategory = ((await db.getKv(KV_LAST_CATEGORY)) as CategoryId | null) ?? 'FOOD';
    set({
      hydrated: true,
      monthlyBudgetGr: household?.monthlyBudgetGr ?? 0,
      householdId: household?.id ?? '',
      entries,
      lastCategory,
    });
  },

  addEntry: async (categoryId, amountGr, note) => {
    const now = new Date().toISOString();
    const entry: BudgetEntry = {
      id: uuid(),
      householdId: get().householdId,
      categoryId,
      amountGr,
      note: note && note.trim() ? note.trim() : null,
      entryDate: todayIso(),
      authorMemberId: 'mem-0001', // P0: single local user; real member id arrives with P1 accounts
      createdAt: now,
      updatedAt: now,
    };
    const db = await getDb();
    await db.upsert(TABLES.entries, entry.id, entry);
    await db.setKv(KV_LAST_CATEGORY, categoryId);
    set((s) => ({ entries: [entry, ...s.entries], lastCategory: categoryId }));
    scheduleHouseholdSync(); // shared-household sync (no-op unless in one)
  },

  updateEntry: async (id, patch) => {
    const current = get().entries.find((e) => e.id === id);
    if (!current) return;
    const normalized =
      'note' in patch ? { ...patch, note: patch.note && patch.note.trim() ? patch.note.trim() : null } : patch;
    const updated: BudgetEntry = { ...current, ...normalized, updatedAt: new Date().toISOString() };
    const db = await getDb();
    await db.upsert(TABLES.entries, id, updated);
    set((s) => ({ entries: s.entries.map((e) => (e.id === id ? updated : e)) }));
    scheduleHouseholdSync();
  },

  removeEntry: async (id) => {
    const db = await getDb();
    await db.remove(TABLES.entries, id);
    set((s) => ({ entries: s.entries.filter((e) => e.id !== id) }));
    scheduleHouseholdSync();
  },

  // The only place the user can change the monthly budget. Needed since the
  // demo seed became DEV-only: a fresh install starts at 0 and would otherwise
  // have no way to ever set it. Persists into the household KV (the source the
  // hydrates read) and refreshes the household store's copy.
  setMonthlyBudget: async (amountGr) => {
    const db = await getDb();
    const raw = await db.getKv(KV_HOUSEHOLD);
    if (!raw) return;
    const household = JSON.parse(raw) as Household;
    const next: Household = { ...household, monthlyBudgetGr: amountGr, updatedAt: new Date().toISOString() };
    await db.setKv(KV_HOUSEHOLD, JSON.stringify(next));
    set({ monthlyBudgetGr: amountGr });
    await useHouseholdStore.getState().hydrate();
  },
}));
